import React, {
  useContext,
  useEffect,
  useReducer,
  useRef,
  useState,
} from "react";
import { makeStyles } from "@material-ui/core/styles";
import toastError from "../../errors/toastError";
import Popover from "@material-ui/core/Popover";
import { MdOutlineForum } from "react-icons/md";
import { PiChatsCircle } from "react-icons/pi";
import { Avatar, Badge, IconButton } from "@material-ui/core";
import api from "../../services/api";
import { isArray } from "lodash";
import { AuthContext } from "../../context/Auth/AuthContext";

import notifySound from "../../assets/chat_notify.mp3";
import useSound from "use-sound";
import { i18n } from "../../translate/i18n";
import { useHistory } from "react-router-dom";
import { format, isSameDay } from "date-fns";
import { socketConnection } from "../../services/socket";
import { getTokens } from "../../styles/tokens";

const useStyles = makeStyles((theme) => {
  const tk = getTokens(theme.palette.type, theme.palette.primary.main);
  const isDark = theme.palette.type === "dark";
  // selo de não lidas igual ao da lista de conversas (verde do WhatsApp)
  const unread = isDark
    ? { bg: "#00a884", fg: "#111b21" }
    : { bg: "#25d366", fg: "#ffffff" };
  return {
    // o tema limita todo Popover a 300px; este painel precisa de mais largura
    popoverPaper: {
      width: "360px !important",
      maxWidth: "calc(100vw - 24px) !important",
      marginTop: 8,
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      borderRadius: tk.radius.md,
      backgroundColor: tk.paper,
    },
    panelHeader: {
      flex: "none",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 8,
      padding: "14px 16px 12px",
      borderBottom: `1px solid ${tk.line}`,
    },
    panelTitle: {
      margin: 0,
      fontSize: 15,
      fontWeight: 700,
      lineHeight: "20px",
      color: tk.text,
    },
    list: {
      flex: 1,
      minHeight: 0,
      maxHeight: "min(420px, calc(100vh - 160px))",
      overflowY: "auto",
      ...theme.scrollbarStyles,
    },
    item: {
      width: "100%",
      display: "flex",
      alignItems: "center",
      gap: 12,
      padding: "10px 16px",
      border: 0,
      borderBottom: `1px solid ${tk.lineRow}`,
      background: "transparent",
      color: "inherit",
      font: "inherit",
      textAlign: "left",
      cursor: "pointer",
      transition: "background-color 150ms ease",
      "&:hover": { backgroundColor: tk.muted },
      "&:focus-visible": {
        outline: `2px solid ${tk.accent}`,
        outlineOffset: -2,
      },
      "&:last-child": { borderBottom: 0 },
    },
    itemAvatar: {
      flex: "none",
      width: 40,
      height: 40,
    },
    itemBody: {
      flex: 1,
      minWidth: 0,
      display: "flex",
      flexDirection: "column",
      gap: 2,
    },
    itemRow: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      minWidth: 0,
    },
    title: {
      flex: 1,
      minWidth: 0,
      overflow: "hidden",
      color: tk.text,
      fontSize: 14,
      fontWeight: 600,
      lineHeight: "20px",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    message: {
      flex: 1,
      minWidth: 0,
      overflow: "hidden",
      color: tk.textSecondary,
      fontSize: 12.5,
      lineHeight: "17px",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    time: {
      flex: "none",
      color: tk.textMuted,
      fontSize: 11,
      lineHeight: "16px",
      fontVariantNumeric: "tabular-nums",
    },
    timeUnread: {
      color: unread.bg,
      fontWeight: 700,
    },
    unread: {
      flex: "none",
      minWidth: 20,
      height: 20,
      padding: "0 6px",
      borderRadius: 10,
      backgroundColor: unread.bg,
      color: unread.fg,
      fontSize: 11.5,
      fontWeight: 700,
      lineHeight: "20px",
      textAlign: "center",
      fontVariantNumeric: "tabular-nums",
    },
    empty: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 8,
      padding: "32px 20px 36px",
      color: tk.textSecondary,
      fontSize: 13,
      textAlign: "center",
      "& svg": {
        fontSize: 34,
        color: tk.textMuted,
      },
    },
    dot: {
      backgroundColor: unread.bg,
    },
  };
});

// hora (hoje) ou data curta, como na lista de conversas
const shortTime = (value) => {
  const date = new Date(value);
  if (!value || isNaN(date.getTime())) return "";
  return isSameDay(date, new Date())
    ? format(date, "HH:mm")
    : format(date, "dd/MM/yyyy");
};

const reducer = (state, action) => {
  if (action.type === "LOAD_CHATS") {
    const chats = action.payload;
    const newChats = [];

    if (isArray(chats)) {
      chats.forEach((chat) => {
        const chatIndex = state.findIndex((u) => u.id === chat.id);
        if (chatIndex !== -1) {
          state[chatIndex] = chat;
        } else {
          newChats.push(chat);
        }
      });
    }

    return [...state, ...newChats];
  }

  if (action.type === "UPDATE_CHATS") {
    const chat = action.payload;
    const chatIndex = state.findIndex((u) => u.id === chat.id);

    if (chatIndex !== -1) {
      state[chatIndex] = chat;
      return [...state];
    } else {
      return [chat, ...state];
    }
  }

  if (action.type === "DELETE_CHAT") {
    const chatId = action.payload;

    const chatIndex = state.findIndex((u) => u.id === chatId);
    if (chatIndex !== -1) {
      state.splice(chatIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }

  if (action.type === "CHANGE_CHAT") {
    const changedChats = state.map((chat) => {
      if (chat.id === action.payload.chat.id) {
        return action.payload.chat;
      }
      return chat;
    });
    return changedChats;
  }
};

export default function ChatPopover(volume) {
  const classes = useStyles();

  //   const socketManager = useContext(SocketContext);
  const { user: loggedUser, socket } = useContext(AuthContext);
  const history = useHistory();

  const historyRef = useRef(history);
  const [loading, setLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [searchParam] = useState("");
  const [chats, dispatch] = useReducer(reducer, []);
  const [invisible, setInvisible] = useState(true);
  const [play] = useSound(notifySound, volume);
  const soundAlertRef = useRef();

  useEffect(() => {
    soundAlertRef.current = play;

    if (!("Notification" in window)) {
      console.log("This browser doesn't support notifications");
    } else {
      Notification.requestPermission();
    }
  }, [play]);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchChats();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParam, pageNumber]);

  useEffect(() => {
    if (loggedUser.id) {
      const companyId = loggedUser.companyId;
      const socket = socketConnection({ companyId, userId: loggedUser.id });

      const onCompanyChatPopover = (data) => {
        if (data.action === "new-message") {
          dispatch({ type: "CHANGE_CHAT", payload: data });
          console.log(data);
          if (
            data.newMessage.senderId !== loggedUser.id &&
            data.chat.users.find((user) => user.userId === loggedUser.id)
          ) {
            soundAlertRef.current();

            // Check if notifications are permitted
            if (Notification.permission === "granted") {
              const notification = new Notification(
                "Chat interno nº " + data.newMessage.chatId,
                {
                  body: `${data.newMessage.sender.name}: ${
                    data.newMessage.message
                  } - ${format(new Date(), "HH:mm")}`,
                  icon: data.newMessage.sender.profileImage,
                }
              );

              notification.onclick = (e) => {
                e.preventDefault();
                window.focus();
                historyRef.current.push(`/chats/${data.newMessage.chatId}`);
              };
            }
          }
        }
        if (data.action === "update") {
          dispatch({ type: "CHANGE_CHAT", payload: data });
        }
      };

      socket.on(`company-${companyId}-chat`, onCompanyChatPopover);

      return () => {
        socket.off(`company-${companyId}-chat`, onCompanyChatPopover);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedUser.id, loggedUser.companyId]);

  useEffect(() => {
    let unreadsCount = 0;
    if (chats.length > 0) {
      for (let chat of chats) {
        for (let chatUser of chat.users) {
          if (chatUser.userId === loggedUser.id) {
            unreadsCount += chatUser.unreads;
          }
        }
      }
    }
    if (unreadsCount > 0) {
      setInvisible(false);
    } else {
      setInvisible(true);
    }
  }, [chats, loggedUser.id]);

  const fetchChats = async () => {
    try {
      const { data } = await api.get("/chats/", {
        params: { searchParam, pageNumber },
      });
      dispatch({ type: "LOAD_CHATS", payload: data.records });
      setHasMore(data.hasMore);
      setLoading(false);
    } catch (err) {
      toastError(err);
    }
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    setInvisible(true);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const goToMessages = (chat) => {
    window.location.href = `/chats/${chat.uuid}`;
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  const chatName = (item) =>
    typeof item.lastMessage === "string"
      ? item.title || "Chat"
      : item.lastMessage?.sender?.name || item.title || "Chat";

  const chatSnippet = (item) =>
    typeof item.lastMessage === "string"
      ? item.lastMessage
      : item.lastMessage?.message || "Nenhuma mensagem";

  const chatUnreads = (item) =>
    item.users?.find((chatUser) => chatUser.userId === loggedUser.id)
      ?.unreads || 0;

  return (
    <div>
      <IconButton
        aria-describedby={id}
        aria-label="Chat interno"
        aria-haspopup="true"
        color="inherit"
        onClick={handleClick}
      >
        <Badge
          color="secondary"
          variant="dot"
          classes={{ dot: classes.dot }}
          invisible={invisible}
        >
          <PiChatsCircle />
        </Badge>
      </IconButton>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        classes={{ paper: classes.popoverPaper }}
      >
        <div className={classes.panelHeader}>
          {/* TODO i18n */}
          <h2 className={classes.panelTitle}>Chat interno</h2>
        </div>
        <div className={classes.list} onScroll={handleScroll}>
          {isArray(chats) &&
            chats.map((item, key) => {
              const unreads = chatUnreads(item);
              const name = chatName(item);
              return (
                <button
                  type="button"
                  key={key}
                  className={classes.item}
                  onClick={() => goToMessages(item)}
                >
                  <Avatar className={classes.itemAvatar}>
                    {String(name).trim().charAt(0).toUpperCase() || "?"}
                  </Avatar>
                  <span className={classes.itemBody}>
                    <span className={classes.itemRow}>
                      <span className={classes.title}>{name}</span>
                      <span
                        className={`${classes.time} ${
                          unreads > 0 ? classes.timeUnread : ""
                        }`}
                      >
                        {shortTime(item.updatedAt)}
                      </span>
                    </span>
                    <span className={classes.itemRow}>
                      <span className={classes.message}>
                        {chatSnippet(item)}
                      </span>
                      {unreads > 0 && (
                        <span className={classes.unread}>{unreads}</span>
                      )}
                    </span>
                  </span>
                </button>
              );
            })}
          {isArray(chats) && chats.length === 0 && (
            <div className={classes.empty}>
              <MdOutlineForum />
              <span>{i18n.t("mainDrawer.appBar.notRegister")}</span>
            </div>
          )}
        </div>
      </Popover>
    </div>
  );
}
